/* eslint-disable no-unused-vars */
import React, { useCallback, useMemo, useEffect, useState, useLayoutEffect } from 'react';
import _ from 'underscore';
import queryString from 'query-string';
import { mergeClasses } from '@fluentui/react-components';
import { Overlay } from '@bingads-webui-react/fabric-control';
import { AdsStudioSpinner } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { Mode } from '@bingads-webui-react/app-layout-container';

import { useEditImageState } from './model/use-edit-image-state';
import { ViewName } from './model/utils';
import { ImageAdjustView, AdPlacementListView, SaveButtonInSummaryBar, NavigationView, BackgroundChangeView, PlacementEditView, PlacementEditViewV2, UpdateConfirmDialog, OptimizeView, SizeListView } from './views';
import { useStyles } from './style';

export const EditImagePageComponent = ({
  dataService,
  imageId,
  search,
  i18n,
  pagePerfMarker: editPagePerfMarker,
  permissions,
  logger,
  getSmartCroppings,
  MultiLineEllipsisText,
  navigation,
  isAIGCRegionRestricted,
  notificationApi,
  getAssetEnhancementSettings,
  changeLayout,
  setDataLoaded,
  // New Inline Asset Editor
  defaultAdType,
  customizedConfig, // ensure not to random add any customized flags
  updateBackgroundImage,
  updateCropSetting,
  patchPlacementList,
  setPlacementList,
  defaultPlacement,
  onToggleAspectRatio,
  canExcludeAspectRatio,
  includedTypeList,
}) => {
  const classes = useStyles();
  const queries = queryString.parse(search);
  const {
    folderId = '', editImageUrl = '', initialCurrentView = '',
  } = queries || {};

  const isAdsStudioOptimizeTabEnabled = useMemo(() => _.get(permissions, ['dynamic', 'AdsStudioOptimizeTab']), [permissions]);

  const {
    originalImage,
    resultImage,
    currentView,
    setCurrentView,
    filterIndex,
    setFilterIndex,
    backgroundBlurEnabled,
    toggleBackgroundBlur,
    changeBackgroundBlurLevel,
    backgroundBlurLevel,
    getImagesWithFilter,
    loadingStatus,
    initialAssetLoading,
    saveImageDirectly,
    imagesWithFilter,
    placementList,
    firstImage,
    onEditPlacement,
    editingPlacementInfo,
    adType,
    publisher,
    showRequiredOnly,
    previewTexts,
    previewImage,
    maxCropBox,
    saveCropPlacement,
    cancelCropPlacement,
    switchAdType,
    switchShowRequiredOnly,
    // Background change view states
    getSegmentations,
    generateBackground,
    generateMultipleImageBackgrounds,
    drawColorBackground,
    imageUrlWithColoredBackground,
    imageUrlWithoutBackground,
    imageUrlWithGeneratedBackground,
    setImageUrlWithGeneratedBackground,
    getMaskValueAtPoint,
    segmentationMasks,
    setSelectedImageSrc,
    selectedImageSrc, // the image after user selected objects
    selectedMasks,
    setSelectedMasks,
    maskValues,
    fetchSegmentationError,
    isFetchingSegmentations,
    replaceBackground,
    saveAsCopy,
    isStockImage,
    isImageUpdated,
    disableSave,
    haveCroppingTasks,
    isConfirmDialogOpen,
    setIsConfirmDialogOpen,
    onConfirmDialogSave,
    allSizeInvalidRatioList,
    saveButtonTooltip,
    updateAdPlacementImage,
    backgroundChangedResult,
    switchPublisher,
  } = useEditImageState({
    imageId,
    folderId,
    dataService,
    editImageUrl,
    i18n,
    perfMarker: editPagePerfMarker,
    permissions,
    logger,
    getSmartCroppings,
    navigation,
    initialCurrentView,
    getAssetEnhancementSettings,
    setDataLoaded,
    // New Inline Asset Editor
    defaultAdType,
    patchPlacementList,
    // Optimizer Tab
    isAdsStudioOptimizeTabEnabled,
  });

  const adjustButtonTooltip = useMemo(() => {
    if (isStockImage) {
      return i18n.getString(_TL_('You cannot adjust or edit Shutterstock images.'));
    }
    return undefined;
  }, [i18n, isStockImage]);

  const isSaveAsCopyDisabled = useMemo(() => isStockImage || !isImageUpdated, [isImageUpdated, isStockImage]);

  const baseButtonText = useMemo(() => {
    if (isImageUpdated) {
      return i18n.getString(_TL_('Save as copy'));
    }
    return i18n.getString(_TL_('Save'));
  }, [i18n, isImageUpdated]);

  const onBaseButtonClick = useCallback(() => {
    if (isImageUpdated) {
      saveAsCopy();
    } else {
      saveImageDirectly();
    }
  }, [isImageUpdated, saveAsCopy, saveImageDirectly]);

  const goToBackgroundChangeView = useCallback(() => {
    setCurrentView(ViewName.BackgroundChangeView);
  }, [setCurrentView]);

  const goToImageAdjustView = useCallback(() => {
    setCurrentView(ViewName.ImageAdjustView);
    setImageUrlWithGeneratedBackground(null);
  }, [setCurrentView, setImageUrlWithGeneratedBackground]);

  const gotoPlacementEditView = useCallback((placement) => {
    onEditPlacement(placement);
    setCurrentView(ViewName.PlacementEditView);
  }, [setCurrentView, onEditPlacement]);

  const goToPlacementListView = useCallback(() => {
    setCurrentView(ViewName.PlacementListView);
  }, [setCurrentView]);

  useEffect(() => {
    setClarityTag({ LoadAdsStudioImageEditingPage: true });
  }, []);

  useEffect(() => {
    if (_.isFunction(updateBackgroundImage) && backgroundChangedResult) {
      updateBackgroundImage(backgroundChangedResult);
    }
  }, [backgroundChangedResult, updateBackgroundImage]);

  // The two useEffects below are used to navigate to specific PlacementEditView for inline editor
  useEffect(() => {
    if (_.isFunction(setPlacementList) && !!placementList) {
      setPlacementList(placementList);
    }
  }, [placementList, setPlacementList]);

  useEffect(() => {
    if (defaultPlacement) {
      gotoPlacementEditView(defaultPlacement);
    }
  }, [defaultPlacement, gotoPlacementEditView]);

  useLayoutEffect(() => {
    if (changeLayout) {
      changeLayout(
        {
          nav: {
            mode: Mode.Hidden,
          },
        },
        true, // isPageConfig
        true // isPageConfigPartialUpdate
      );
    }
  }, [changeLayout]);

  return (
    <div className={mergeClasses('edit--page-container', classes.editImagePage, 'edit-image-page')}>
      <SaveButtonInSummaryBar
        i18n={i18n}
        onSaveDirectly={saveImageDirectly}
        onSaveAsCopy={saveAsCopy}
        isSaveAsCopyDisabled={isSaveAsCopyDisabled}
        isSaveDisabled={disableSave}
        haveCroppingTasks={haveCroppingTasks}
        isBaseButton
        baseButtonText={baseButtonText}
        onBaseButtonClick={onBaseButtonClick}
        saveButtonTooltip={saveButtonTooltip}
      />
      {!initialAssetLoading &&
        <div className={mergeClasses(classes.navigationView, 'navigation-view')}>
          <NavigationView
            i18n={i18n}
            currentView={currentView}
            setCurrentView={setCurrentView}
            isAdjustButtonDisabled={isStockImage}
            adjustButtonTooltip={adjustButtonTooltip}
            updateAdPlacementImage={updateAdPlacementImage}
            isAdsStudioOptimizeTabEnabled={isAdsStudioOptimizeTabEnabled}
          />
        </div>
      }
      <div className={classes.navigationPlaceholder} />
      <div className={classes.contentWrapper}>
        { currentView === ViewName.OptimizeView &&
          <OptimizeView
            i18n={i18n}
            resultImage={resultImage}
            setCurrentView={setCurrentView}
            updateAdPlacementImage={updateAdPlacementImage}
            goToImageAdjustView={goToImageAdjustView}
            goToBackgroundChangeView={goToBackgroundChangeView}
            isAdjustButtonDisabled={isStockImage}
          />
        }
        {
          currentView === ViewName.PlacementListView && (isAdsStudioOptimizeTabEnabled ?
            <SizeListView
              placementList={placementList}
              firstImage={firstImage}
              onEditPlacement={gotoPlacementEditView}
              adType={adType}
              publisher={publisher}
              showRequiredOnly={showRequiredOnly}
              previewTexts={previewTexts}
              i18n={i18n}
              switchAdType={switchAdType}
              switchShowRequiredOnly={switchShowRequiredOnly}
              switchPublisher={switchPublisher}
              permissions={permissions}
              {...(customizedConfig && customizedConfig.AdPlacementListView)}
            /> : <AdPlacementListView
              placementList={placementList}
              firstImage={firstImage}
              onEditPlacement={gotoPlacementEditView}
              adType={adType}
              showRequiredOnly={showRequiredOnly}
              previewTexts={previewTexts}
              i18n={i18n}
              switchAdType={switchAdType}
              switchShowRequiredOnly={switchShowRequiredOnly}
              {...(customizedConfig && customizedConfig.AdPlacementListView)}
            />
          )
        }
        {
          currentView === ViewName.PlacementEditView && (isAdsStudioOptimizeTabEnabled ? <PlacementEditViewV2
            i18n={i18n}
            firstImage={firstImage}
            adType={adType}
            {...editingPlacementInfo}
            logger={logger}
            previewImage={previewImage}
            previewTexts={previewTexts}
            goToPlacementListView={goToPlacementListView}
            maxCropBox={maxCropBox}
            onSave={saveCropPlacement}
            onCancel={cancelCropPlacement}
            onToggleAspectRatio={onToggleAspectRatio}
            canExcludeAspectRatio={canExcludeAspectRatio}
            includedTypeList={includedTypeList}
            permissions={permissions}
          /> : <PlacementEditView
            i18n={i18n}
            firstImage={firstImage}
            adType={adType}
            {...editingPlacementInfo}
            logger={logger}
            previewImage={previewImage}
            previewTexts={previewTexts}
            goToPlacementListView={goToPlacementListView}
            maxCropBox={maxCropBox}
            updateCropSetting={updateCropSetting}
            onSave={saveCropPlacement}
            onCancel={cancelCropPlacement}
            permissions={permissions}
          />)
        }
        {
          currentView === ViewName.ImageAdjustView &&
          <ImageAdjustView
            resultImage={resultImage}
            toggleBackgroundBlur={toggleBackgroundBlur}
            setFilterIndex={setFilterIndex}
            backgroundBlurEnabled={backgroundBlurEnabled}
            changeBackgroundBlurLevel={changeBackgroundBlurLevel}
            backgroundBlurLevel={backgroundBlurLevel}
            imagesWithFilter={imagesWithFilter}
            i18n={i18n}
            originalImage={originalImage}
            getImagesWithFilter={getImagesWithFilter}
            goToBackgroundChangeView={goToBackgroundChangeView}
            filterIndex={filterIndex}
            isAIGCRegionRestricted={isAIGCRegionRestricted}
            notificationApi={notificationApi}
            permissions={permissions}
          />
        }
        {
          currentView === ViewName.BackgroundChangeView &&
          <BackgroundChangeView
            i18n={i18n}
            getSegmentations={getSegmentations}
            generateBackground={generateBackground}
            generateMultipleImageBackgrounds={generateMultipleImageBackgrounds}
            MultiLineEllipsisText={MultiLineEllipsisText}
            drawColorBackground={drawColorBackground}
            imageUrlWithColoredBackground={imageUrlWithColoredBackground}
            imageUrlWithoutBackground={imageUrlWithoutBackground}
            imageUrlWithGeneratedBackground={imageUrlWithGeneratedBackground}
            setImageUrlWithGeneratedBackground={setImageUrlWithGeneratedBackground}
            goToImageAdjustView={goToImageAdjustView}
            resultImage={resultImage} // The original image is the result image from the image adjust view
            originalImage={originalImage}
            logger={logger}
            getMaskValueAtPoint={getMaskValueAtPoint}
            segmentationMasks={segmentationMasks}
            maskValues={maskValues}
            setSelectedImageSrc={setSelectedImageSrc}
            selectedImageSrc={selectedImageSrc}
            selectedMasks={selectedMasks}
            setSelectedMasks={setSelectedMasks}
            fetchSegmentationError={fetchSegmentationError}
            isFetchingSegmentations={isFetchingSegmentations}
            onApplyImage={replaceBackground}
            permissions={permissions}
          />
        }
        <UpdateConfirmDialog
          i18n={i18n}
          open={isConfirmDialogOpen}
          setOpen={setIsConfirmDialogOpen}
          onSave={onConfirmDialogSave}
          invalidRatioList={allSizeInvalidRatioList}
        />
        {
          loadingStatus &&
          <Overlay>
            <AdsStudioSpinner className="page-load-spinner" useCustomSize customStyle={{ margin: 200 }} />
          </Overlay>
        }
      </div>
    </div>
  );
};
